import { CopyIcon, KeyIcon, Trash2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { LockedCard } from '@/components/feature/LockedCard';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import {
  type ApiTokenCreated,
  type ApiTokenSummary,
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
} from '@/lib/hooks/useApiTokens';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { hasFeature } from '@/lib/permissions';
import { cn } from '@/lib/utils';

export function SettingsApiTokensPage() {
  const { data: perms } = usePermissions();
  const apiAccess = hasFeature(perms, 'api_access');

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">API tokens</h1>
        <p className="mt-1 text-sm text-slate-500">
          Bearer tokens for programmatic access to the public API. Tokens are shown in full only once on creation.
        </p>
      </header>

      <div className="mt-6">
        {!perms ? (
          <Card><p className="text-sm text-slate-500">Loading…</p></Card>
        ) : !apiAccess ? (
          <LockedCard
            title="Public API access"
            description="Generate API tokens to call the /api/v1 endpoints from your code. Bearer auth, per-token rate limits, webhooks."
            requiredTier="scale"
          />
        ) : (
          <TokensTable />
        )}
      </div>
    </div>
  );
}

function TokensTable() {
  const { data, isLoading } = useApiTokens();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const onCreate = async (body: { name: string; expires_in_days?: number }) => {
    const token = await create.mutateAsync(body);
    setCreated(token);
    setShowCreate(false);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Active tokens</CardTitle>
          <CardDescription>
            Authorize requests with <code className="font-mono text-xs">Authorization: Bearer atn_live_…</code>
          </CardDescription>
        </CardHeader>

        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : data && data.items.length === 0 ? (
          <p className="text-sm text-slate-500">No tokens yet. Create one to start calling the API.</p>
        ) : (
          <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Token</th>
                  <th className="px-3 py-2 font-medium">Last used</th>
                  <th className="px-3 py-2 font-medium">Expires</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {data?.items.map((t) => <TokenRow key={t.id} token={t} onRevoke={() => revoke.mutate(t.id)} />)}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button onClick={() => setShowCreate(true)} disabled={create.isPending}>
            <KeyIcon className="h-4 w-4" />
            New token
          </Button>
        </div>
      </Card>

      {showCreate && <CreateTokenDialog onSubmit={onCreate} onClose={() => setShowCreate(false)} pending={create.isPending} error={create.error as ApiError | Error | null} />}
      {created && <RevealTokenModal created={created} onClose={() => setCreated(null)} />}
    </>
  );
}

function TokenRow({ token, onRevoke }: { token: ApiTokenSummary; onRevoke: () => void }) {
  const isRevoked = !!token.revoked_at;
  const isExpired = !!token.expires_at && new Date(token.expires_at) <= new Date();

  return (
    <tr className={cn(isRevoked && 'opacity-50')}>
      <td className="px-3 py-2 font-medium">{token.name}</td>
      <td className="px-3 py-2 font-mono text-xs">{token.token_preview}</td>
      <td className="px-3 py-2 text-xs text-slate-500">
        {token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'Never'}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">
        {token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'No expiry'}
      </td>
      <td className="px-3 py-2 text-xs">
        {isRevoked ? (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-500 dark:bg-slate-800">Revoked</span>
        ) : isExpired ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900 dark:bg-amber-950 dark:text-amber-200">Expired</span>
        ) : (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">Active</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {!isRevoked && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm(`Revoke token "${token.name}"? This cannot be undone.`)) onRevoke();
            }}
            aria-label={`Revoke ${token.name}`}
          >
            <Trash2Icon className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
}

function CreateTokenDialog({
  onSubmit,
  onClose,
  pending,
  error,
}: {
  onSubmit: (body: { name: string; expires_in_days?: number }) => void;
  onClose: () => void;
  pending: boolean;
  error: ApiError | Error | null;
}) {
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState<string>('90');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      expires_in_days: expiry ? parseInt(expiry, 10) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>Create an API token</CardTitle>
          <CardDescription>You'll see the full token exactly once after creation.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="token-name">Name</Label>
            <Input id="token-name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="CI deploy script" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="token-expiry">Expires in (days)</Label>
            <Input id="token-expiry" type="number" min={1} max={365} value={expiry} onChange={(e) => setExpiry(e.target.value)} placeholder="90" />
            <p className="text-xs text-slate-500">Leave blank for a non-expiring token (not recommended).</p>
          </div>
          {error && (
            <p className="text-sm text-[var(--color-critical)]">
              {error instanceof ApiError ? JSON.stringify(error.detail) : (error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {pending ? 'Creating…' : 'Create token'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function RevealTokenModal({ created, onClose }: { created: ApiTokenCreated; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(created.raw_token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Token created — copy it now</CardTitle>
          <CardDescription>This is the only time you'll see <strong>{created.name}</strong> in full. Athena stores only a hash.</CardDescription>
        </CardHeader>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          ⚠️ If you lose this value, revoke this token and create a new one.
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
          <code className="flex-1 truncate font-mono text-xs">{created.raw_token}</code>
          <button
            type="button"
            onClick={copy}
            className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            aria-label="Copy token"
          >
            <CopyIcon className="h-4 w-4" />
          </button>
          {copied && <span className="text-xs text-[var(--color-success)]">copied</span>}
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={onClose}>I've saved it</Button>
        </div>
      </Card>
    </div>
  );
}
